/**
 * KVM (Prox) API client.
 *
 * Talks to the Prox **Customer API** directly (canonical contract: the OpenAPI spec
 * at /api/v1/openapi.json). The UI is scoped to an **entitlement** ("service"); a
 * service exists before any VM, so `provisioning_state` drives which screen shows.
 *
 * AUTH: each call is authorized with a short-lived **per-entitlement JWT** minted by
 * amapi (billing) — `POST kvm_token { id }`, where `id` is the service id (same call
 * shape as vserver_reboot, authed by the session). The JWT pins the entitlement, so
 * Prox requests carry only `Authorization: Bearer <jwt>` (no X-Entitlement-Id).
 * Tokens are cached per service and re-minted on expiry / a 401.
 *
 * Config: VUE_APP_VDS_API_ENDPOINT - Customer API base. REQUIRED, no default:
 *   .env             https://vmapi.greengeeks.com/api/v1  (production)
 *   .env.development /vdsproxy/api/v1                     (dev-server proxy)
 */
import axios from 'axios'
import amapi from '@/api/amapi'

const BASE = process.env.VUE_APP_VDS_API_ENDPOINT

const MiB = 1024 * 1024
const GiB = 1024 * 1024 * 1024

const http = axios.create({ baseURL: BASE })

// Fail fast, and name the variable, when a build has no endpoint configured.
// An unset BASE makes axios issue RELATIVE requests against the AMUI host,
// which serves only the static build. Production Apache neither 404s nor errors
// quickly there - it hangs and returns an HTML 500 - so a missing config value
// presented as a broken API. Reject with the cause instead. See GG-996.
http.interceptors.request.use((config) => {
  if (!BASE) {
    return Promise.reject(new Error(
      'VUE_APP_VDS_API_ENDPOINT is not set in this build, so the Prox Customer ' +
      'API is unreachable. Set it in the .env file for this build mode.'
    ))
  }
  return config
})

// Per-service JWT cache: { [serviceId]: { token, expiresAt } }.
const tokenCache = {}

// Mint (or reuse) the access token for a service — POST kvm_token { id } through
// amapi (same shape as vserver_reboot; authed by the session). Cached until ~expiry.
async function getToken (serviceId, force = false) {
  const now = Date.now()
  const cached = tokenCache[serviceId]
  if (!force && cached && cached.expiresAt - now > 10000) return cached.token
  const res = await amapi.makecall('kvm_token', { id: serviceId })
  // payload is an array of token records ({ access_token, token_type, expires_in,
  // expires_at, entitlement_id, ... }); use the first.
  const rec = Array.isArray(res && res.payload) ? res.payload[0] : ((res && res.payload) || res || {})
  const token = rec && (rec.access_token || rec.token)
  if (!token) throw new Error('Could not get a server access token.')
  const ttl = Number(rec.expires_in) || 0
  tokenCache[serviceId] = { token, expiresAt: ttl ? now + ttl * 1000 : now + 5 * 60 * 1000 }
  return token
}

const authHeaders = async (serviceId) => ({ Authorization: `Bearer ${await getToken(serviceId)}` })

// Turn axios/Prox failures into a plain Error with a human-readable `.message` (and
// `.status`). On a 401 we re-mint the token once and retry (the JWT may have just
// expired). Prox 4xx bodies follow the Error/ValidationError envelope.
http.interceptors.response.use(
  response => response,
  async (error) => {
    const cfg = error.config || {}
    const res = error.response
    if (res && res.status === 401 && cfg._serviceId != null && !cfg._retried) {
      cfg._retried = true
      try {
        const token = await getToken(cfg._serviceId, true)
        cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${token}` }
        return http.request(cfg)
      } catch (e) { /* fall through to the normal error mapping */ }
    }
    const data = res && res.data
    let message = (data && data.message) || error.message || 'The request failed.'
    if (res && res.status === 401) message = 'Not authorized — your server access token is missing or invalid.'
    if (res && res.status === 400 && !(data && data.message)) message = 'The service could not be found or the request was invalid.'
    if (data && data.errors) {
      const fields = Object.values(data.errors).flat().filter(Boolean)
      if (fields.length) message += ` (${fields.join(', ')})`
    }
    const err = new Error(message)
    err.status = res && res.status
    err.data = data
    return Promise.reject(err)
  }
)

// → the coarse online/offline the power UI reasons about. The live /vm/status
// reading wins whenever we have one: the DB `vm.status` is NOT kept in step with
// power state (power actions and in-guest shutdowns never update it), so OR-ing
// it in showed a stopped VM as Running. The DB value is only a fallback for when
// the live call failed.
const powerStateOf = (dbStatus, vmStatus) => {
  const live = vmStatus && (vmStatus.qmpstatus || vmStatus.status)
  return (live || dbStatus) === 'running' ? 'online' : 'offline'
}

// provisioning_state → a label the list + statusColor()/serviceManagable() expect.
const provStatusLabel = (state) => {
  switch (state) {
    case 'provisioned': return 'Active'
    case 'provisioning': return 'Provisioning'
    case 'unprovisioned': return 'Setup required'
    default: return state || '—'
  }
}

// The billing lifecycle status wins over provisioning when not active, so a
// suspended/terminated service is labelled as such (statusColor handles both).
const displayStatus = (entStatus, provState) => {
  if (entStatus === 'suspended') return 'Suspended'
  if (entStatus === 'terminated') return 'Cancelled'
  return provStatusLabel(provState)
}

// Merge a /service object (+ optional /vm/status telemetry) into the flat shape
// the KVM views consume. Sizes come from the plan/spec; live usage from /vm/status.
// Still unavailable from the Customer API (kept null → "—"): billing/renewal dates.
const mapServer = (service, vmStatus) => {
  const plan = service.plan || {}
  const vm = service.vm || {}
  const addrs = vm.ip_addresses || []
  const primary = addrs.find(ip => ip.is_primary) || addrs[0]
  const ramTotal = vmStatus && vmStatus.maxmem != null
    ? vmStatus.maxmem
    : (plan.mem_mb != null ? plan.mem_mb * MiB : null)
  const diskGb = plan.disk_gb != null ? plan.disk_gb : (vm.spec && vm.spec.disk_gb)
  return {
    // String id so it routes cleanly as a :hostingid route param (the sidebar's
    // placeholder substitution only runs for string values).
    id: String(service.entitlement_id),
    entitlement_id: service.entitlement_id,
    provisioning_state: service.provisioning_state,
    // The VM's own lifecycle status (provisioning → running/stopped → error/...).
    // Distinct from provisioning_state: a 'provisioned' service can still have a
    // VM that is mid-build ('provisioning') and not yet started.
    vm_status: (vmStatus && vmStatus.status) || vm.status || null,
    vm_id: vm.id || null,
    // The name shown for the server: the customer's label, else its hostname.
    label: service.label || service.hostname || vm.name || plan.name || `Service #${service.entitlement_id}`,
    // Just the customer-set label (null = none), for the rename dialog.
    custom_label: service.label || null,
    // The server's hostname (the customer's choice, else the generated VM name).
    hostname: service.hostname || vm.name || '—',
    entitlement_status: service.status || null, // active | suspended | terminated
    status: displayStatus(service.status, service.provisioning_state),
    power_state: powerStateOf(vm.status, vmStatus),
    primary_ip: (primary && primary.address) || '—',
    ips: addrs.map(ip => ip.address),
    plan: plan.name || '—',
    // Display name ("Chicago"); fall back to the slug from older backends.
    location: service.region_name || service.region || '—',
    os: service.os || service.app || '—',
    app: service.app || null,
    // Customer-facing post-install info for the deployed App (null for bare OS).
    // markdown + params have their {{ variables }} resolved server-side.
    app_info: service.app_info || null,
    // In-guest deployment progress (InstallProgress) — distinct from vm_status; the
    // VM can be running while the app is still installing. Null for bare-OS / no VM.
    install: service.install || null,
    domain: service.domain || null,
    effective_domain: service.effective_domain || null,
    // The no-ip hostname the API offers when the service has no domain of its own,
    // for "Generate a free subdomain". Server-side so the no-ip domain is
    // configurable per environment; null means the feature is off and the UI must
    // offer nothing. Present before the VM exists, derived from the reserved IP.
    // @since GG-994
    suggested_domain: service.suggested_domain || null,
    vcpu: plan.vcpu != null ? plan.vcpu : ((vm.spec && vm.spec.vcpu) || null),
    ram_total: ramTotal,
    ram_used: vmStatus && vmStatus.mem != null ? vmStatus.mem : null,
    // Prefer the VM's actual disk size (maxdisk) over the plan's nominal size.
    disk_total: vmStatus && vmStatus.maxdisk ? vmStatus.maxdisk : (diskGb != null ? diskGb * GiB : null),
    // Cumulative network transfer since boot (bytes).
    net_in: vmStatus && vmStatus.netin != null ? vmStatus.netin : null,
    net_out: vmStatus && vmStatus.netout != null ? vmStatus.netout : null,
    cpu_used_percent: vmStatus && vmStatus.cpu != null ? Math.round(vmStatus.cpu * 100) : 0,
    uptime: vmStatus ? vmStatus.uptime : null,
    created: vm.created_at || null,
    last_error: vm.last_error || null,
    // Billing fields — only present when capabilities.billing_details is true.
    expires_at: service.expires_at || null,
    auto_renew: service.auto_renew != null ? service.auto_renew : null,
    renewalterm: '—',
    renewaldate: null, // parseRenewalDate(null) → "N/A"
    // The SSH keys injected into this service's VM (id/name/fingerprint).
    ssh_keys: service.ssh_keys || [],
    // Which optional features this backend can serve — drives the UI's
    // light-up / grey-out of cards (instead of guessing from field presence).
    capabilities: service.capabilities || {},
    // Docker Manager offered for this service (capability on AND its App opted in).
    docker_enabled: !!(service.docker && service.docker.enabled),
    // The app chosen with this server, installed once its build is done:
    // { state: pending|installing|installed|failed, name, stack, url, error, note } or null.
    docker_app_install: service.docker_app_install || null,
  }
}

export default {
  /** Raw service overview (GET /service). */
  async getService (id) {
    const { data } = await http.get('/service', { headers: await authHeaders(id), _serviceId: id })
    return data
  },

  /**
   * Service for the detail UI: /service merged with live /vm/status telemetry
   * (only fetched once provisioned). Carries `provisioning_state` so the view
   * can branch between the picker, the provisioning spinner, and the dashboard.
   */
  async getServer (id) {
    const service = await this.getService(id)
    let vmStatus = null
    if (service.provisioning_state === 'provisioned') {
      try {
        vmStatus = (await http.get('/vm/status', { headers: await authHeaders(id), _serviceId: id })).data
      } catch (e) {
        // status can transiently fail right after provisioning; keep /service data.
      }
    }
    const mapped = mapServer(service, vmStatus)
    // Keep the id we were routed/tokened with (the billing service id) as the id the
    // views use for sub-navigation, so every sub-page mints a token for this service.
    mapped.id = String(id)
    return mapped
  },

  /**
   * Check whether a domain's A records point at this service's IP (GET
   * /service/dns-check). Advisory — used to warn, before deploy, that SSL won't
   * issue otherwise. Works before the VM exists (uses the reserved IP).
   * @param {string} [domain] domain to check (defaults to the service's domain)
   * @returns {Promise<{domain, target_ip, resolved: string[], matches, checked_at}>}
   */
  async checkDns (id, domain) {
    const params = domain ? { domain } : {}
    const { data } = await http.get('/service/dns-check', { headers: await authHeaders(id), _serviceId: id, params })
    return data
  },

  /** OS/App choices for an unprovisioned service (GET /catalog). Region is
   *  fixed by billing and NOT part of the catalog; read it from GET /service. */
  async getCatalog (id) {
    const { data } = await http.get('/catalog', { headers: await authHeaders(id), _serviceId: id })
    return data
  },

  /**
   * Commit the OS/App choice and start the build (POST /provision). Region is
   * fixed at the entitlement, so it is NOT sent here.
   * @param {Object} choice { os } OR { app } (exactly one), plus optional
   *   `options` (catalog option selections, e.g. { version }), `ssh_key_ids`, and
   *   `domain` (overrides the billing default; used in app URLs / cloud-init).
   */
  async provision (id, choice) {
    const { data } = await http.post('/provision', choice, { headers: await authHeaders(id), _serviceId: id })
    return data
  },

  /**
   * Rebuild a provisioned service (POST /reprovision). ⚠️ DESTRUCTIVE: wipes
   * the disk. The IP and region are retained. Omit os/app to reinstall the same OS.
   * @param {Object} [choice] {} | { os } | { app }, plus optional `options`,
   *   `ssh_key_ids`, and `domain` (omit any to keep the current value; `domain: ''`
   *   clears it).
   */
  async reprovision (id, choice = {}) {
    const { data } = await http.post('/reprovision', choice, { headers: await authHeaders(id), _serviceId: id })
    return data
  },

  /**
   * The customer's SSH keys (GET /ssh-keys). Shared across all
   * their services; the set injected into a VM at provision/rebuild.
   */
  async getSshKeys (id) {
    const { data } = await http.get('/ssh-keys', { headers: await authHeaders(id), _serviceId: id })
    return (data && data.data) || []
  },

  /** Add an SSH key (POST /ssh-keys). 201 (new) / 200 (existing fingerprint). */
  async addSshKey (id, { name, public_key: publicKey }) {
    const { data } = await http.post('/ssh-keys', { name, public_key: publicKey }, { headers: await authHeaders(id), _serviceId: id })
    return data && data.data
  },

  /** Remove an SSH key by id (DELETE /ssh-keys/{id}). */
  async deleteSshKey (id, keyId) {
    await http.delete(`/ssh-keys/${keyId}`, { headers: await authHeaders(id), _serviceId: id })
  },

  /** Set (or clear, with '') the server's display label — panel only. → the label now set */
  async setLabel (id, label) {
    const { data } = await http.put('/service/label', { label }, { headers: await authHeaders(id), _serviceId: id })
    return data.label
  },

  /** Change the server's hostname — applied on the running server. → the hostname now set */
  async setHostname (id, hostname) {
    const { data } = await http.put('/vm/hostname', { hostname }, { headers: await authHeaders(id), _serviceId: id })
    return data.hostname
  },

  /**
   * This server's live on/off state for each account key (GET /vm/ssh-keys),
   * read from the login user's authorized_keys via the guest agent.
   * → { user, keys: [{ id, name, fingerprint, enabled }] }
   */
  async getServerSshKeys (id) {
    const { data } = await http.get('/vm/ssh-keys', { headers: await authHeaders(id), _serviceId: id })
    return data
  },

  /** Turn an account key on/off on this server (PUT /vm/ssh-keys/{key}), live. */
  async setServerSshKey (id, keyId, enabled) {
    const { data } = await http.put(`/vm/ssh-keys/${keyId}`, { enabled: !!enabled },
      { headers: await authHeaders(id), _serviceId: id })
    return data
  },

  /** Issue a power action (POST /vm/power). */
  async powerAction (id, action) {
    const { data } = await http.post('/vm/power', { action }, { headers: await authHeaders(id), _serviceId: id })
    return data
  },

  /**
   * Broker a console session (POST /vm/console).
   * @param {string} id entitlement id
   * @param {'vnc'|'serial'} [type='vnc'] 'vnc' → graphical noVNC (RFB `password`);
   *   'serial' → text console for xterm.js (attach straight to `websocket`;
   *   `password` is empty, the proxy auth's server-side, auto-logs-in as root).
   * @returns {Promise<{vm_id, type, websocket, password, expires_in}>}
   */
  async getConsole (id, type = 'vnc') {
    const body = type === 'serial' ? { type: 'serial' } : {}
    const { data } = await http.post('/vm/console', body, { headers: await authHeaders(id), _serviceId: id })
    // Local dev only: the API hands back an absolute wss:// URL on the console
    // proxy's own host, which refuses a localhost Origin. Re-point it at the dev
    // server's proxy prefix (vue.config.js) so the socket goes same-origin.
    const consoleProxy = process.env.VUE_APP_VDS_CONSOLE_PROXY
    if (consoleProxy && data && data.websocket) {
      const upstream = new URL(data.websocket)
      const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      data.websocket = `${scheme}//${window.location.host}${consoleProxy}${upstream.pathname}${upstream.search}`
    }
    return data
  },

  // ---- Docker Manager (/vm/docker/*) --------------------------------------
  // Only for services with docker_enabled. Long actions (run, stack deploy/
  // actions/remove, template deploy) return { operation: { id, label, state } };
  // poll dockerOperation(id) until state is 'succeeded' or 'failed'.
  // Names/refs are encodeURIComponent'd; the backend re-validates everything.

  /** Is Docker usable right now? { available, reason, version, compose_version } */
  async dockerStatus (id) {
    const { data } = await http.get('/vm/docker', { headers: await authHeaders(id), _serviceId: id })
    return data
  },
  async dockerContainers (id) {
    const { data } = await http.get('/vm/docker/containers', { headers: await authHeaders(id), _serviceId: id })
    return data.data || []
  },
  async dockerLogs (id, container, tail = 200) {
    const { data } = await http.get(`/vm/docker/containers/${encodeURIComponent(container)}/logs`,
      { params: { tail }, headers: await authHeaders(id), _serviceId: id })
    return data
  },
  /** action ∈ start | stop | restart | remove */
  async dockerContainerAction (id, container, action) {
    const { data } = await http.post(`/vm/docker/containers/${encodeURIComponent(container)}/${action}`, {},
      { headers: await authHeaders(id), _serviceId: id })
    return data
  },
  /** spec: { image, name?, restart?, ports?: [], env?: [{key,value}], volumes?: [], command?: [] } */
  async dockerRun (id, spec) {
    const { data } = await http.post('/vm/docker/containers', spec, { headers: await authHeaders(id), _serviceId: id })
    return data.operation
  },
  async dockerStacks (id) {
    const { data } = await http.get('/vm/docker/stacks', { headers: await authHeaders(id), _serviceId: id })
    return data.data || []
  },
  /** { name, compose, containers[] } */
  async dockerStack (id, stack) {
    const { data } = await http.get(`/vm/docker/stacks/${encodeURIComponent(stack)}`, { headers: await authHeaders(id), _serviceId: id })
    return data
  },
  /** Save the compose file and deploy it. */
  async dockerSaveStack (id, stack, compose) {
    const { data } = await http.put(`/vm/docker/stacks/${encodeURIComponent(stack)}`, { compose },
      { headers: await authHeaders(id), _serviceId: id })
    return data.operation
  },
  /** action ∈ up | down | restart | pull | stop | start | update (pull + recreate) */
  async dockerStackAction (id, stack, action) {
    const { data } = await http.post(`/vm/docker/stacks/${encodeURIComponent(stack)}/${action}`, {},
      { headers: await authHeaders(id), _serviceId: id })
    return data.operation
  },
  /**
   * The images an app runs and their installed versions; check=true also asks
   * each registry what the tag points to now (a few seconds per image).
   * → [{ image, tag, services, installed, latest, update_available, error }]
   */
  async dockerVersions (id, stack, check = false) {
    const { data } = await http.get(`/vm/docker/stacks/${encodeURIComponent(stack)}/versions`,
      { params: check ? { check: 1 } : {}, headers: await authHeaders(id), _serviceId: id })
    return data.data || []
  },
  async dockerRemoveStack (id, stack, volumes = false) {
    const { data } = await http.delete(`/vm/docker/stacks/${encodeURIComponent(stack)}`,
      { params: volumes ? { volumes: 1 } : {}, headers: await authHeaders(id), _serviceId: id })
    return data.operation
  },
  /**
   * Browsers waiting for a one-time approval in a stack's app (OpenClaw).
   * → [{ request_id, client, platform, remote_ip, requested_at, approvable }]
   */
  async dockerPairingRequests (id, stack) {
    const { data } = await http.get(`/vm/docker/stacks/${encodeURIComponent(stack)}/pairing`,
      { headers: await authHeaders(id), _serviceId: id })
    return data.data || []
  },
  /** decision ∈ approve | reject */
  async dockerDecidePairing (id, stack, requestId, decision) {
    await http.post(`/vm/docker/stacks/${encodeURIComponent(stack)}/pairing/${encodeURIComponent(requestId)}/${decision}`, {},
      { headers: await authHeaders(id), _serviceId: id })
  },
  async dockerTemplates (id) {
    const { data } = await http.get('/vm/docker/templates', { headers: await authHeaders(id), _serviceId: id })
    return data.data || []
  },
  /** The same catalog with no server (no token), for choosing an app before its
   *  server: → { templates, freeDomain } where freeDomain ("ggsvm.net") is what
   *  every server's free name sits under (<8 hex of its IP>.<freeDomain>). */
  async publicDockerTemplates () {
    const { data } = await http.get('/public/docker-templates')
    return { templates: data.data || [], freeDomain: data.free_domain || null }
  },
  /**
   * → { operation, generated: {KEY: value} (show once), post_deploy, route }
   * `hostname` (optional) also publishes the app through the managed web proxy.
   */
  async dockerDeployTemplate (id, slug, stack, variables, hostname = null, path = '/') {
    const body = { stack, variables }
    if (hostname) {
      body.hostname = hostname
      body.path = path || '/'
    }
    const { data } = await http.post(`/vm/docker/templates/${encodeURIComponent(slug)}/deploy`, body,
      { headers: await authHeaders(id), _serviceId: id })
    return data
  },

  // Managed web proxy (Caddy on 80/443 routing domains to app ports).
  /** { installed, running, email, free_domain, server_ip, routes: [{hostname, port, dns_ok}] } */
  async dockerProxy (id) {
    const { data } = await http.get('/vm/docker/proxy', { headers: await authHeaders(id), _serviceId: id })
    return data
  },
  async dockerProxyInstall (id, email) {
    const { data } = await http.post('/vm/docker/proxy', { email }, { headers: await authHeaders(id), _serviceId: id })
    return data.operation
  },
  /** → { route: {hostname, path, port}, applied }. path "/" = the whole domain. */
  async dockerProxyAddRoute (id, hostname, port, path = '/') {
    const { data } = await http.post('/vm/docker/proxy/routes', { hostname, port: Number(port), path: path || '/' },
      { headers: await authHeaders(id), _serviceId: id })
    return data
  },
  async dockerProxyRemoveRoute (id, hostname, path = '/') {
    const { data } = await http.delete(`/vm/docker/proxy/routes/${encodeURIComponent(hostname)}`,
      { params: { path: path || '/' }, headers: await authHeaders(id), _serviceId: id })
    return data
  },
  /** { id, label, state: running|succeeded|failed, exit_code, started_at, finished_at, output } */
  async dockerOperation (id, opId) {
    const { data } = await http.get(`/vm/docker/operations/${encodeURIComponent(opId)}`, { headers: await authHeaders(id), _serviceId: id })
    return data
  },

  /**
   * Set the VM's root password (POST /vm/password-reset) to the customer-supplied
   * `password` (8–128 chars; never stored). Applied instantly via the guest agent
   * when it is responding (`method` 'agent', no reboot). If the agent is down it
   * needs an offline disk edit that briefly restarts the VM — only attempted when
   * `allowReboot` is true (`method` 'offline', `reboot: true`); otherwise the call
   * is refused with 409 (retry with allowReboot).
   * @param {string} password the new root password
   * @param {boolean} [allowReboot=false] permit a brief restart if the offline path is needed
   * @returns {Promise<{vm_id, method:'agent'|'offline', reboot}>}
   */
  async resetPassword (id, password, allowReboot = false) {
    const body = { password, allow_reboot: allowReboot }
    const { data } = await http.post('/vm/password-reset', body, { headers: await authHeaders(id), _serviceId: id })
    return data
  },

  /**
   * The server's root password (GET /vm/root-password), read from the server
   * itself. `current: false` = changed on the server since; `available: false`
   * = none on file (set one with resetPassword).
   * @returns {Promise<{available:boolean, password:?string, set_at:?string, current:?boolean}>}
   */
  async getRootPassword (id) {
    const { data } = await http.get('/vm/root-password', { headers: await authHeaders(id), _serviceId: id })
    return data
  },

  /**
   * The VM firewall — default policy + rules (GET /vm/firewall). Available only
   * when capabilities.firewall is true. Each rule carries `system_managed`
   * (provider-enforced, read-only); the firewall itself cannot be disabled.
   * @returns {Promise<{enable, policy_in, policy_out, rules: Array}>}
   */
  async getFirewall (id) {
    const { data } = await http.get('/vm/firewall', { headers: await authHeaders(id), _serviceId: id })
    return data
  },

  /** Add a firewall rule (POST /vm/firewall/rules). `rule` is a FirewallRuleRequest
   *  ({ type:'in'|'out', action:'ACCEPT'|'DROP'|'REJECT', proto?, dport?, sport?,
   *  source?, dest?, enable?, comment?, immediate? }). `immediate` clears existing
   *  connections so a drop-causing change applies to already-open ones too. */
  async addFirewallRule (id, rule) {
    const { data } = await http.post('/vm/firewall/rules', rule, { headers: await authHeaders(id), _serviceId: id })
    return data
  },

  /** Update a firewall rule by position handle (PUT /vm/firewall/rules/{pos}).
   *  Partial — any subset of rule fields (plus optional `immediate`). Provider
   *  rules return 403. */
  async updateFirewallRule (id, pos, patch) {
    const { data } = await http.put(`/vm/firewall/rules/${pos}`, patch, { headers: await authHeaders(id), _serviceId: id })
    return data
  },

  /** Delete a firewall rule by position handle (DELETE /vm/firewall/rules/{pos}).
   *  `immediate` (query) clears existing connections if removing the rule could newly
   *  drop traffic. Provider rules return 403. */
  async deleteFirewallRule (id, pos, immediate = false) {
    const params = immediate ? { immediate: true } : {}
    const { data } = await http.delete(`/vm/firewall/rules/${pos}`, { headers: await authHeaders(id), _serviceId: id, params })
    return data
  },

  /** Set the default firewall policy (PUT /vm/firewall/policy). At least one of
   *  policy_in/policy_out (plus optional `immediate`). The firewall cannot be
   *  disabled. */
  async setFirewallPolicy (id, policy) {
    const { data } = await http.put('/vm/firewall/policy', policy, { headers: await authHeaders(id), _serviceId: id })
    return data
  },

  /**
   * Live cloud-init install log tail (GET /vm/deploy-log). Available only when
   * capabilities.deploy_logs is true AND the app opted in (else `available:false`,
   * hide the panel). Returns the redacted tail (~64KB) — replace it into a scrolling
   * view. Poll while install.state is 'installing'; stop when `complete` is true.
   * @returns {Promise<{available, complete, truncated, content}>}
   */
  async getDeployLog (id) {
    const { data } = await http.get('/vm/deploy-log', { headers: await authHeaders(id), _serviceId: id })
    return data
  },

  /**
   * Metric history for sparklines (Customer API — GET /vm/metrics). Available only
   * when capabilities.metrics_history is true. Each series is [[unix_ts, value], …].
   * @param {string} range one of 1h | 24h | 7d | 30d | 1y
   */
  async getMetrics (id, range = '24h') {
    const { data } = await http.get('/vm/metrics', { headers: await authHeaders(id), _serviceId: id, params: { range } })
    return data
  },

  /** How full the main disk is (GET /vm/disk, from the guest agent): { mountpoint, total, used, free } bytes. */
  async getDisk (id) {
    const { data } = await http.get('/vm/disk', { headers: await authHeaders(id), _serviceId: id })
    return data
  },
}
